/**
 * NotificationBell — Phase 2E agent in-app notifications dropdown.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export default function NotificationBell() {
  const { get, post } = useApi();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    get('/me/notifications')
      .then(r => { setItems(r.items || []); setUnread(r.unread_count || 0); })
      .catch(() => {});
  }, [get]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const onOpen = (open) => {
    if (open && unread > 0) {
      post('/me/notifications/read-all').then(() => setUnread(0)).catch(() => {});
    }
  };

  return (
    <DropdownMenu onOpenChange={onOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" data-testid="agent-bell-btn">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="agent-bell-count">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-96 overflow-y-auto p-0" data-testid="agent-notifications-list">
        {items.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground text-center">No notifications yet.</p>
        ) : items.map(n => (
          <button key={n.inbox_id}
                  className={`w-full text-left px-3 py-2.5 border-b last:border-0 hover:bg-muted/50 ${!n.read ? 'bg-amber-50/60' : ''}`}
                  onClick={() => n.link && navigate(n.link)}
                  data-testid={`notification-${n.inbox_id}`}>
            <p className="text-sm font-medium">{n.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
          </button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
